import {
  HOME3_DESTINATION_LABEL_KEY,
  type Home3Destination,
  type Home3DestinationFilter,
  type Home3ReadState,
} from '../../../lib/home3-feed';
import { AVATAR_PALETTE, avatarInitials, avatarSlotFor } from '../../../lib/avatar-color';
import { useBasePath } from '../../../lib/use-base-path';
import { useTranslation } from '../../../lib/i18n';
import { Avatar, ScrollArea, Typography } from '../ui';
import styles from './Home3Categories.module.css';

/**
 * The category row — "All" and the six destinations, one tile each, on one line
 * (Figma `19697:59325`).
 *
 * It replaced a grid of six cards that each carried their destination's whole per-state breakdown.
 * The tiles say one thing instead: **how much is new since you last looked at that category**, as a
 * disc on the corner, which clears when you press it. A board that showed four counts per card asked
 * the reader to do the arithmetic; a single unread count is the question they actually have.
 *
 * ## A horizontal slider that always peeks the next tile (Figma 20077-39081)
 *
 * The tiles are a FIXED 60x60 (the frame's own size) in a horizontal `ScrollArea`, not equal shares of
 * the column — so on a phone, where seven 60+14 tiles overflow the width, the next tile is always
 * partly visible and the row reads as a slider you can push. The bar is `hidden` (the affordance is the
 * peeking tile, like `MobileAgentCards`); on a wide desktop column where all seven fit, there is simply
 * nothing to scroll. The label sits BELOW the tile box, not inside it — only the 60x60 box lifts/recedes
 * with selection.
 *
 * ## The art is the frame's, and only Messages is live
 *
 * Five of the six are illustrations exported from the frames into `public/illustrations/home3/` —
 * bespoke drawings rather than icons, which is the one case the icon rule exempts from lucide. They
 * are named from the TSX as `<img src>` off `useBasePath()`, never from the stylesheet: a `url()` in
 * a CSS module resolves against the CDN, where `public/` does not exist, and paints nothing with no
 * error (see the package CLAUDE.md).
 *
 * **Messages draws the real person.** The frame puts a photo there and that is what it means: the
 * avatar of whoever wrote most recently, from the contact book, falling back to their initials and
 * then to the generic mark. It is the one tile whose art is data.
 */
const ART: Record<Exclude<Home3Destination, 'outreach'>, string> = {
  tasks: 'tasks',
  routines: 'routines',
  goals: 'goals',
  apps: 'apps',
  files: 'files',
};

/** Left to right, as the frame lays them out — not `HOME3_DESTINATIONS`' own order. */
export const HOME3_CATEGORY_ORDER: readonly Home3Destination[] = [
  'tasks',
  'routines',
  'goals',
  'outreach',
  'apps',
  'files',
];

export function Home3Categories({
  unread,
  selected,
  onSelect,
  onSelectAll,
  readState,
  listId,
  messengers = [],
}: {
  /** New-since-last-visit per category — see `useHome3CategoryVisits`. Zero means no disc. */
  unread: Record<Home3Destination, number>;
  selected: Home3DestinationFilter;
  /** The tile tapped. The caller selects it, and marks that category seen. */
  onSelect: (destination: Home3Destination) => void;
  /** The "All" tile — clears the category filter. */
  onSelectAll: () => void;
  /** Until `ready` no tile wears a disc: a count of 0 and "not known yet" are different facts. */
  readState: Home3ReadState;
  /** The list region the tiles filter, for `aria-controls`. */
  listId: string;
  /**
   * Whoever wrote most recently — the Messages tile's face. Absent until the contacts read lands.
   * `seed` is what picks their colour off the avatar ramp: their id where there is one, so the colour
   * survives a rename.
   */
  /**
   * Whoever wrote most recently, newest first — the Messages tile's two faces. The tile draws a
   * cluster of two whatever this holds, so a short list is padded with the design's own mark rather
   * than changing the tile's shape.
   */
  messengers?: { name: string; photoUrl?: string; seed: string }[];
}) {
  const { t } = useTranslation();
  const basePath = useBasePath();

  return (
    // A horizontal slider (Figma 20077-39081): the fixed 60x60 tiles overflow a phone's width, so the
    // next tile always peeks and the row reads as scrollable. `bar="hidden"` — the peeking tile is the
    // affordance, as on the other swipe rows. The radiogroup is the inner track so the scroller stays a
    // pure scroll container.
    <ScrollArea axis="horizontal" bar="hidden" className={styles.scroller}>
      <div className={styles.row} role="radiogroup" aria-label={t('home.overview.filterDestination')}>
      {/* "All" is the row's first option and now carries its own art — the stacked-cards mark the frame
          draws for it (Figma 19848-66034), over the same label the other tiles use. */}
      <button
        type="button"
        role="radio"
        aria-checked={selected === null}
        aria-controls={listId}
        className={`${styles.cat}${selected === null ? ` ${styles.catOn}` : ''}`}
        onClick={onSelectAll}
      >
        <span className={styles.tile}>
          <span className={styles.art}>
            <img src={`${basePath}/illustrations/home3/all.svg`} alt="" width={40} height={40} />
          </span>
        </span>
        {/* Same selected rule as the six below: "All" is the chosen tile whenever nothing else is. */}
        <Typography
          variant={selected === null ? 'tiny-semi-bold' : 'p3'}
          color={selected === null ? 'primary' : 'secondary'}
          component="span"
          className={styles.label}
          noWrap
        >
          {t('home.overview.filter.all')}
        </Typography>
      </button>

      {HOME3_CATEGORY_ORDER.map((destination) => {
        const on = selected === destination;
        const count = readState === 'ready' ? unread[destination] : 0;
        const name = t(HOME3_DESTINATION_LABEL_KEY[destination]);
        return (
          <button
            key={destination}
            type="button"
            role="radio"
            aria-checked={on}
            aria-controls={listId}
            className={`${styles.cat}${on ? ` ${styles.catOn}` : ''}`}
            onClick={() => onSelect(destination)}
          >
            <span className={styles.tile}>
            <span className={styles.art}>
              {destination === 'outreach' ? (
                /* ## Messages is a CLUSTER OF TWO, always — not one avatar
                   
                   The frame draws two overlapping bubbles (Figma 20018:118832 / 19848-66690: a photo
                   behind-left, a cyan initials avatar in front-right), and that shape IS the mark.
                   It used to collapse to a single avatar as soon as anybody had written, so the tile
                   changed shape depending on the data and stopped matching the design the moment it
                   had something real to show (Design QA, Caro).
                   
                   So the cluster is the constant and the PEOPLE are what vary: the two most recent
                   senders where there are two, one real and the design's own second where there is
                   one, and both of the design's where the contacts read has nothing yet. Every bubble
                   is the shared DS `Avatar`, so initials, geometry and ink come from the primitive. */
                <span className={styles.messages}>
                  <Avatar
                    initials={messengers[0] ? avatarInitials(messengers[0].name) : ''}
                    /* A REAL sender with no photo falls back to their own initials, which is the DS
                       Avatar's own behaviour — `?? messages-photo.png` here handed them the design's
                       placeholder FACE instead, so a named contact rendered as a stranger. The
                       placeholder is only for the bubble that stands in for nobody. */
                    photoUrl={messengers[0] ? messengers[0].photoUrl : `${basePath}/illustrations/home3/messages-photo.png`}
                    size={32}
                    /* A ramp slot keyed on the person, never the default `brand` gradient — that one is
                       the SIGNED-IN user's and this tile draws someone else. Seeded on the id where
                       there is one, so a contact keeps their colour when their name changes. */
                    color={messengers[0] ? avatarSlotFor(messengers[0].seed) : AVATAR_PALETTE[0]}
                  />
                  <Avatar
                    initials={messengers[1] ? avatarInitials(messengers[1].name) : 'CN'}
                    photoUrl={messengers[1]?.photoUrl}
                    size={32}
                    color={messengers[1] ? avatarSlotFor(messengers[1].seed) : AVATAR_PALETTE[7]}
                    className={styles.messagesFront}
                  />
                </span>
              ) : (
                <img src={`${basePath}/illustrations/home3/${ART[destination]}.svg`} alt="" width={40} height={40} />
              )}
            </span>
            {count > 0 && (
              <Typography variant="tiny-semi-bold" color="inherit" component="span" className={styles.badge}>
                {/* The count the reader has not seen. It is the tile's own, so pressing one tile
                    leaves the others' discs alone. */}
                {count}
              </Typography>
            )}
            </span>
            {/* The CHOSEN tile's label is the primary ink at the SemiBold tier; the rest sit
                secondary and Regular (Design QA, Caro — Figma 20018:106262). The weight is a role
                either way, never a literal, so both carry the mobile restore. */}
            <Typography
              variant={on ? 'tiny-semi-bold' : 'p3'}
              color={on ? 'primary' : 'secondary'}
              component="span"
              className={styles.label}
              noWrap
            >
              {name}
            </Typography>
          </button>
        );
      })}
      </div>
    </ScrollArea>
  );
}
